import WisdomNavbar from "components/navbars/WisdomNabvar";
import { useAuth } from "context/AuthContext";
import { useLanguage } from "context/LanguageContext";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { assignTeacherToClasses } from "services/admins";
import {
    fetchWisdomClasses,
    fetchWisdomRelatedSchools,
    fetchWisdomRelatedTeachers,
    fetchWisdomStages,
    fetchWisdomTeacherClasses
} from "services/wisdom/wisdomData";

const getClassIdFromRecord = (session) => {
    const id = session?.class_id;
    return id != null ? Number(id) : null;
};

const WisdomAdminTeacherClassAssign = () => {
    const { userInfo } = useAuth();
    const { language } = useLanguage();
    const [schools, setSchools] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [classes, setClasses] = useState([]);
    const [stages, setStages] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState("");
    const [selectedTeacher, setSelectedTeacher] = useState("");
    const [selectedStage, setSelectedStage] = useState("");
    const [selectedClassIds, setSelectedClassIds] = useState([]);
    const [assignedClassIds, setAssignedClassIds] = useState([]);
    const [loadingTeacherClasses, setLoadingTeacherClasses] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const isMultiSchoolAdmin = userInfo?.organization_id === 13;
    const effectiveSchoolId = isMultiSchoolAdmin ? selectedSchool : userInfo?.organization_id;

    useEffect(() => {
        const loadingTeachers = async () => {
            const [schoolResponse, teacherResponse, classResponse, stageResponse] = await Promise.all([
                fetchWisdomRelatedSchools(),
                fetchWisdomRelatedTeachers(),
                fetchWisdomClasses(),
                fetchWisdomStages()
            ]);
            setSchools(schoolResponse);
            setTeachers(teacherResponse);
            setClasses(classResponse);
            setStages(stageResponse);
        };

        loadingTeachers();
    }, []);

    useEffect(() => {
        if (!selectedTeacher) {
            setAssignedClassIds([]);
            setSelectedClassIds([]);
            return;
        }

        const loadTeacherClasses = async () => {
            setLoadingTeacherClasses(true);
            try {
                const teacherClasses = await fetchWisdomTeacherClasses(selectedTeacher);
                const ids = teacherClasses
                    .map(getClassIdFromRecord)
                    .filter((id) => id != null);
                setAssignedClassIds(ids);
                setSelectedClassIds(ids);
            } catch {
                toast.error(language ? "Failed to load teacher classes" : "فشل تحميل فصول المعلم");
                setAssignedClassIds([]);
                setSelectedClassIds([]);
            } finally {
                setLoadingTeacherClasses(false);
            }
        };

        loadTeacherClasses();
    }, [selectedTeacher, language]);

    const filteredTeachers = useMemo(
        () =>
            teachers.filter((t) =>
                effectiveSchoolId === "" || effectiveSchoolId == null
                    ? true
                    : t?.employee?.organization_id === Number(effectiveSchoolId)
            ),
        [teachers, effectiveSchoolId]
    );

    const filteredClasses = useMemo(
        () =>
            classes.filter(
                (c) =>
                    (effectiveSchoolId === "" || effectiveSchoolId == null
                        ? true
                        : c?.classRoom?.organization_id === Number(effectiveSchoolId)) &&
                    (selectedStage === "" ? true : c.stage_id === Number(selectedStage))
            ),
        [classes, selectedStage, effectiveSchoolId]
    );

    const isClassIdSelected = (classId) =>
        selectedClassIds.some((id) => Number(id) === Number(classId));

    const isClassIdAssigned = (classId) =>
        assignedClassIds.some((id) => Number(id) === Number(classId));

    const allFilteredSelected =
        filteredClasses.length > 0 &&
        filteredClasses.every((c) => isClassIdSelected(c.id));

    const toggleClass = (classId) => {
        const normalizedId = Number(classId);
        setSelectedClassIds((prev) =>
            prev.some((id) => Number(id) === normalizedId)
                ? prev.filter((id) => Number(id) !== normalizedId)
                : [...prev, normalizedId]
        );
    };

    const toggleSelectAll = (checked) => {
        if (checked) {
            const idsToAdd = filteredClasses.map((c) => Number(c.id));
            setSelectedClassIds((prev) => Array.from(new Set([...prev, ...idsToAdd])));
        } else {
            const idsToRemove = new Set(filteredClasses.map((c) => Number(c.id)));
            setSelectedClassIds((prev) => prev.filter((id) => !idsToRemove.has(Number(id))));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSubmitting(true);
            await assignTeacherToClasses({
                teacher_id: Number(selectedTeacher),
                class_ids: selectedClassIds.map((id) => Number(id)),
            });
            toast.success(language ? "Classes assigned successfully" : "تم تعيين الفصول بنجاح");
            setAssignedClassIds([...selectedClassIds]);
        } catch {
            toast.error(language ? "Failed to assign classes" : "فشل تعيين الفصول");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <WisdomNavbar />
            <div className="max-w-xl mx-auto px-4 py-8" dir={language ? "ltr" : "rtl"}>
                <div className="bg-white rounded-2xl shadow p-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-6">
                        {language ? "Assign Teacher to Classes" : "تعيين المعلم للفصول"}
                    </h1>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* School */}
                        {isMultiSchoolAdmin && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {language ? "School" : "مدرسة"}
                                </label>
                                <select
                                    value={selectedSchool}
                                    onChange={(e) => {
                                        setSelectedSchool(e.target.value);
                                        setSelectedTeacher("");
                                    }}
                                    className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                >
                                    <option value="">
                                        {language ? "All schools" : "كل المدارس"}
                                    </option>
                                    {schools.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Teacher */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {language ? "Teacher" : "معلم"}
                            </label>
                            <select
                                value={selectedTeacher}
                                onChange={(e) => setSelectedTeacher(e.target.value)}
                                className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                                required
                            >
                                <option value="" disabled>
                                    {language ? "Select teacher" : "اختر معلم"}
                                </option>
                                {filteredTeachers.map((teacher) => (
                                    <option key={teacher.id} value={teacher.id}>
                                        {teacher?.employee?.first_name} {teacher?.employee?.middle_name} {teacher?.employee?.last_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Stage filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {language ? "Stage" : "المرحلة التعليمية"}
                            </label>
                            <select
                                value={selectedStage}
                                onChange={(e) => setSelectedStage(e.target.value)}
                                className="w-full border rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-watomsBlue"
                            >
                                <option value="">
                                    {language ? "All stages" : "كل المراحل"}
                                </option>
                                {stages.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Classes */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    {language ? "Classes" : "الفصول"}
                                </label>

                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={allFilteredSelected}
                                        onChange={(e) => toggleSelectAll(e.target.checked)}
                                        disabled={filteredClasses.length === 0}
                                    />
                                    {language ? "Select all" : "اختيار الكل"}
                                </label>
                            </div>

                            <div className="border rounded-xl p-3 max-h-64 overflow-y-auto space-y-2">
                                {loadingTeacherClasses && (
                                    <p className="text-sm text-gray-500">
                                        {language ? "Loading assigned classes..." : "جاري تحميل الفصول المعينة..."}
                                    </p>
                                )}
                                {!loadingTeacherClasses && filteredClasses.length === 0 && (
                                    <p className="text-sm text-gray-500">
                                        {language ? "No classes found" : "لا يوجد فصول"}
                                    </p>
                                )}
                                {!loadingTeacherClasses && filteredClasses.map((c) => {
                                    const isAssigned = isClassIdAssigned(c.id);

                                    return (
                                        <label
                                            key={c.id}
                                            className={`flex items-center gap-2 text-sm text-gray-700 rounded-lg px-2 py-1 ${
                                                isAssigned ? "bg-green-50 border border-green-200" : ""
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isClassIdSelected(c.id)}
                                                onChange={() => toggleClass(c.id)}
                                                disabled={!selectedTeacher}
                                            />
                                            <span className="flex-1">{c.name}</span>
                                            {isAssigned && (
                                                <span className="text-xs text-green-700">
                                                    {language ? "Assigned" : "معين"}
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            className="w-full py-2 rounded-xl bg-watomsBlue text-white hover:opacity-90 transition disabled:opacity-50"
                            disabled={!selectedTeacher || selectedClassIds.length === 0 || submitting || loadingTeacherClasses}
                        >
                            {submitting
                                ? (language ? "Assigning..." : "جاري التعيين...")
                                : (language ? "Assign Classes" : "تعيين الفصول")}
                        </button>
                    </form>
                </div>
            </div>
        </>
    )
};
export default WisdomAdminTeacherClassAssign;
